import type { AccountKind } from '@projections/engine';
import { useFieldArray } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import { AllocationEditor } from '../fields/AllocationEditor';
import type { SectionProps } from '../sectionProps';

/**
 * ACCOUNTS section — a `useFieldArray` over `accounts`. Each row edits an `Account`: the `kind`
 * select (the Thai tax-vehicle taxonomy), `balance`, optional `costBasis` (embedded-gain basis,
 * relevant for taxable kinds) and `dividendYield`, and the allocation / glidePathRef XOR — either a
 * static `AllocationEditor` (asset-class weight rows) OR a select over the plan's named glide paths.
 *
 * The XOR + glidePathRef-resolves validation come from `PlanSchema` (refine/superRefine), surfaced
 * inline on `accounts[i].allocation` and the section-level `accounts` error. Allocation keys align
 * with the offline synthetic asset ids so a freshly-added account runs immediately.
 */

const ACCOUNT_KINDS: ReadonlyArray<{ value: AccountKind; label: string }> = [
  { value: 'taxable_set', label: 'Taxable — SET-listed' },
  { value: 'taxable_foreign', label: 'Taxable — foreign' },
  { value: 'rmf', label: 'RMF' },
  { value: 'ssf', label: 'SSF' },
  { value: 'thai_esg', label: 'Thai ESG' },
  { value: 'pvd', label: 'Provident fund (PVD)' },
  { value: 'cash', label: 'Cash' },
];

export function AccountsSection({ form }: SectionProps): React.ReactElement {
  const { register, control, watch, setValue, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'accounts' });
  const errors = formState.errors.accounts;
  const glidePaths = watch('glidePaths');

  return (
    <section>
      <h2>Accounts</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        Each account's kind drives its tax treatment. Set a static allocation by asset class, or
        attach a named glide path (define glide paths in the Strategy section).
      </p>

      {typeof errors?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {errors.message}
        </p>
      )}
      {typeof errors?.root?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {errors.root.message}
        </p>
      )}

      {fields.map((f, i) => {
        const usesGlide = watch(`accounts.${i}.glidePathRef`) !== undefined;
        const rowErrors = errors?.[i];
        return (
          <div key={f.id} className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <FieldRow>
              <div style={{ flex: 2, minWidth: 180 }}>
                <Field label="Name" error={rowErrors?.name} htmlFor={`accounts.${i}.name`}>
                  <input
                    id={`accounts.${i}.name`}
                    className="input"
                    {...register(`accounts.${i}.name`)}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <Field label="Kind" error={rowErrors?.kind} htmlFor={`accounts.${i}.kind`}>
                  <select
                    id={`accounts.${i}.kind`}
                    className="select"
                    {...register(`accounts.${i}.kind`)}
                  >
                    {ACCOUNT_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FieldRow>

            <FieldRow>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field
                  label="Balance ฿"
                  error={rowErrors?.balance}
                  htmlFor={`accounts.${i}.balance`}
                >
                  <input
                    id={`accounts.${i}.balance`}
                    className="input input--num"
                    type="number"
                    {...register(`accounts.${i}.balance`, { valueAsNumber: true })}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field
                  label="Cost basis ฿"
                  error={rowErrors?.costBasis}
                  htmlFor={`accounts.${i}.costBasis`}
                  hint="Book value; omit = no embedded gain."
                >
                  <input
                    id={`accounts.${i}.costBasis`}
                    className="input input--num"
                    type="number"
                    {...register(`accounts.${i}.costBasis`, {
                      setValueAs: emptyToUndefinedNumber,
                    })}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field
                  label="Dividend yield"
                  error={rowErrors?.dividendYield}
                  htmlFor={`accounts.${i}.dividendYield`}
                  hint="Decimal, e.g. 0.03 = 3%."
                >
                  <input
                    id={`accounts.${i}.dividendYield`}
                    className="input input--num"
                    type="number"
                    step="0.005"
                    {...register(`accounts.${i}.dividendYield`, {
                      setValueAs: emptyToUndefinedNumber,
                    })}
                  />
                </Field>
              </div>
            </FieldRow>

            <hr className="hr" />

            <Field
              label="Holdings source"
              htmlFor={`accounts.${i}.source`}
              hint="Static allocation, or a named glide path."
            >
              <select
                id={`accounts.${i}.source`}
                className="select"
                style={{ maxWidth: 240 }}
                value={usesGlide ? 'glide' : 'allocation'}
                onChange={(e) => {
                  if (e.currentTarget.value === 'glide') {
                    setValue(`accounts.${i}.allocation`, undefined);
                    setValue(`accounts.${i}.glidePathRef`, glidePaths[0]?.id ?? '', {
                      shouldValidate: true,
                    });
                  } else {
                    setValue(`accounts.${i}.glidePathRef`, undefined);
                    setValue(`accounts.${i}.allocation`, { cash: 1 }, { shouldValidate: true });
                  }
                }}
              >
                <option value="allocation">Static allocation</option>
                <option value="glide">Glide path</option>
              </select>
            </Field>

            {usesGlide ? (
              <Field
                label="Glide path"
                error={rowErrors?.glidePathRef}
                htmlFor={`accounts.${i}.glidePathRef`}
              >
                <select
                  id={`accounts.${i}.glidePathRef`}
                  className="select"
                  {...register(`accounts.${i}.glidePathRef`)}
                >
                  {glidePaths.length === 0 && (
                    <option value="">No glide paths defined — add one in Strategy</option>
                  )}
                  {glidePaths.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.id})
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <AllocationEditor
                form={form}
                name={`accounts.${i}.allocation`}
                error={rowErrors?.allocation}
              />
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(i)}
                aria-label={`Remove account ${i + 1}`}
              >
                Remove account
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `acct${fields.length + 1}`,
            name: '',
            kind: 'cash',
            balance: 0,
            allocation: { cash: 1 },
          })
        }
      >
        + Add account
      </button>
    </section>
  );
}

/** Empty string → undefined; otherwise the parsed number (keeps optional numeric fields truly optional). */
function emptyToUndefinedNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}
